import { Code, ConnectError, type Interceptor } from "@connectrpc/connect";

import { getAuthToken, getRefreshToken } from "../shared/lib/authSession";
import { refreshAccessToken } from "./refresh";
import { readJwtClaims } from "./jwt";
import { apiError } from "./apiError";
import { ErrorCode } from "../proto/general";

/** The backend's `ErrorCode` for a failed call. */
function backendErrorCode(error: unknown): ErrorCode | undefined {
  return apiError(error).code;
}

/**
 * True when a failed call means "the server rejected this token".
 *
 * The auth interceptor answers an expired access token with
 * `ERROR_CODE_AUTH_TOKEN_ISSUE`, and some paths flatten the Connect code away
 * from `Unauthenticated`, so the code is read from the ErrorMessage detail
 * rather than the transport status.
 */
function isAuthFailureError(error: unknown): boolean {
  const connectError = ConnectError.from(error);
  return (
    connectError.code === Code.Unauthenticated ||
    (backendErrorCode(connectError)?.startsWith("ERROR_CODE_AUTH_") ?? false)
  );
}

/**
 * Codes meaning the refresh token is finished rather than that the attempt
 * failed: the session was logged out, revoked, or killed after a rotated
 * refresh token was reused. Only re-login gets out of it.
 */
const DEAD_SESSION_CODES = new Set([
  ErrorCode.ERROR_CODE_REFRESH_TOKEN_INVALID,
  ErrorCode.ERROR_CODE_REFRESH_TOKEN_EXPIRED,
  ErrorCode.ERROR_CODE_SESSION_REVOKED,
]);

/**
 * Calls that must never trigger a refresh: the refresh itself and public RPCs.
 *
 * Lowercase, matched against the lowercased `req.method.name` — that is the
 * proto name (`LoginOTP`), not the client's camelCase method (`loginOTP`).
 */
const NO_REFRESH_METHODS = new Set([
  "refreshtoken",
  "login",
  "loginotp",
  "requestotp",
  "forgotpasswordviaotp",
  "resetpassword",
  "selfregistervialink",
]);

const REFRESH_LOCK_NAME = "racoon-auth-refresh";

/**
 * Runs `fn` holding a lock shared by every tab of this origin.
 *
 * Tabs share the tokens in localStorage but not the single-flight below, so
 * without it tabs waking together all spend the same refresh token and the
 * server revokes the session on the second one. Browsers without Web Locks run
 * `fn` unguarded.
 */
async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return fn();
  return await locks.request(REFRESH_LOCK_NAME, fn);
}

/**
 * How early an access token is swapped for a fresh one.
 *
 * Only an optimization — an expired token is refreshed reactively anyway — so
 * the margin stays small: every refresh rotates the refresh token, and doing
 * that more often than needed only widens the window where the tab is closed
 * between receiving a new refresh token and storing it.
 */
const REFRESH_MARGIN_MS = 60_000;

/** Pause after a failed refresh (offline, server down). */
const FAILURE_COOLDOWN_MS = 30_000;

/**
 * True when the JWT expires within `marginMs`.
 *
 * An unreadable token counts as not expiring: the reactive path below handles a
 * rejection anyway, and guessing here would refresh on every single call.
 */
function isJwtExpiringWithin(token: string, marginMs: number): boolean {
  const exp = readJwtClaims(token)?.exp;
  if (typeof exp !== "number") return false;
  return exp * 1000 - Date.now() <= marginMs;
}

let inFlight: Promise<string | null> | null = null;
let disabled = false;
let cooldownUntil = 0;
/** The refresh token stored when refresh was turned off or paused. */
let stoppedFor: string | null = null;

/** Turns refresh off, or pauses it, for the refresh token stored now. */
function stopRefreshing(until: "page-life" | number): void {
  if (until === "page-life") disabled = true;
  else cooldownUntil = until;
  stoppedFor = getRefreshToken();
}

/**
 * Swaps the refresh token for a new pair and returns the new access token, or
 * `null` when there is none to be had.
 *
 * Shared by every caller in this tab — the Connect transport and the REST base
 * query — and single-flighted: a burst of parallel failures triggers one
 * refresh, not one per call. Across tabs the Web Lock does the same job. That
 * matters beyond latency — the server rotates the refresh token and treats a
 * reused one as a stolen session, so two concurrent refreshes with the same
 * token would end the session.
 *
 * Refreshing is abandoned when the refresh token itself is rejected (nothing
 * but re-login fixes that) or when the server answers `Unimplemented` — the RPC
 * exists in the schema before every environment serves it, and rediscovering
 * that per call is pure latency. Either lasts until a new refresh token is
 * stored, i.e. the next login.
 */
export function refreshSession(): Promise<string | null> {
  // A login since then (here or in another tab, without a page reload) stored a
  // new pair, and the reason refresh stopped belongs to the old one.
  if ((disabled || cooldownUntil > 0) && getRefreshToken() !== stoppedFor) {
    disabled = false;
    cooldownUntil = 0;
  }
  if (disabled || Date.now() < cooldownUntil) return Promise.resolve(null);
  if (inFlight) return inFlight;

  // Read before queueing on the lock: if it has changed once the lock is ours,
  // another tab refreshed meanwhile and its tokens are already stored.
  const seenRefreshToken = getRefreshToken();

  const attempt = withRefreshLock(async () => {
    // Admin impersonation swaps only the access token; refreshing with the admin refresh token
    // would overwrite the impersonated access token.
    try {
      if (localStorage.getItem("admin_backup_token")) return null;
    } catch {
      // Ignore storage access failures (SSR / privacy mode).
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;
    if (refreshToken !== seenRefreshToken) return getAuthToken();

    try {
      return await refreshAccessToken(refreshToken);
    } catch (error) {
      // Without Web Locks another tab can still win the race; its tokens are good.
      if (getRefreshToken() !== refreshToken) return getAuthToken();

      const errorCode = backendErrorCode(error);
      if (ConnectError.from(error).code === Code.Unimplemented) {
        stopRefreshing("page-life");
        console.warn("[Transport:Refresh] Server does not implement RefreshToken");
      } else if (errorCode && DEAD_SESSION_CODES.has(errorCode)) {
        stopRefreshing("page-life");
        console.warn(`[Transport:Refresh] Session is gone (${errorCode}), re-login required`);
      } else {
        stopRefreshing(Date.now() + FAILURE_COOLDOWN_MS);
        console.warn("[Transport:Refresh] Token refresh failed:", error);
      }
      return null;
    }
  });

  inFlight = attempt;
  void attempt.finally(() => {
    if (inFlight === attempt) inFlight = null;
  });

  return attempt;
}

/**
 * Keeps the access token fresh so a working session is not cut short by a
 * redirect to login.
 *
 * Two triggers, because one alone is not enough:
 *  - reactive, when a call fails on auth — a token can be rejected before its
 *    own `exp` (server restart, revoked session);
 *  - proactive, when the stored token is about to expire, which spares that
 *    failed round-trip on the first call after a long pause.
 *
 * Sits first in the transport's interceptor list, i.e. outside the header
 * interceptor, so the retry below picks up the refreshed token.
 */
export function tokenRefreshInterceptor(): Interceptor {
  return (next) => async (req) => {
    if (NO_REFRESH_METHODS.has(req.method.name.toLowerCase())) return next(req);

    const token = getAuthToken();
    if (token && isJwtExpiringWithin(token, REFRESH_MARGIN_MS)) await refreshSession();

    const sentToken = getAuthToken();
    try {
      return await next(req);
    } catch (error) {
      if (!isAuthFailureError(error)) throw error;

      // Another tab stored a fresh token while this call was out: retry with it
      // rather than spending the refresh token again.
      const storedToken = getAuthToken();
      const tokenChanged = storedToken !== null && storedToken !== sentToken;
      if (!tokenChanged && !(await refreshSession())) throw error;

      // The header interceptor inside re-reads storage, so this carries the new token.
      return next(req);
    }
  };
}
